import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  OFFLINE_LIBRARY_KEY,
  offlineKey,
  offlineKeyForEvent,
  readOfflineLibrary,
  sortOfflineArticles,
  writeOfflineLibrary,
  type OfflineArticle,
  type OfflineAuthor,
  type OfflineLibrary,
} from '@/lib/offlineLibrary';
import { getTagValue } from '@/lib/nip54';

/**
 * The library is kept in a module-level cache with an explicit
 * subscriber list, rather than in per-component `useState`, so that
 * every mounted consumer stays consistent: saving an article from the
 * article page must immediately update the save button, the profile
 * page's offline section, and any article cards on screen. With
 * independent component state those views would silently disagree
 * until the next remount.
 *
 * `useSyncExternalStore` is the right primitive for this — it's built
 * for subscribing to an external mutable source, and guarantees the
 * snapshot is re-read consistently during render.
 */
let cache: OfflineLibrary | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): OfflineLibrary {
  // The snapshot must be referentially stable between changes, or
  // useSyncExternalStore will loop re-rendering forever.
  cache ??= readOfflineLibrary();
  return cache;
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Keep multiple tabs in sync: `storage` fires in every *other* tab when
// one of them writes, so the library can't drift between tabs.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== OFFLINE_LIBRARY_KEY) return;
    cache = null;
    emit();
  });
}

function mutate(update: (library: OfflineLibrary) => OfflineLibrary): void {
  const next = update(getSnapshot());
  // Write first: if storage is full this throws, and we must NOT update
  // the in-memory cache to claim a save that didn't actually persist.
  writeOfflineLibrary(next);
  cache = next;
  emit();
}

/** Subscribes to the raw library map. */
function useOfflineLibraryStore(): OfflineLibrary {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The locally-saved copy of an article, if there is one — used by
 * `ArticlePage` as a fallback so a saved article still renders when the
 * relays can't be reached (the whole point of saving it).
 *
 * `pubkey` is optional because a reader may land on `/w/:slug` with no
 * author specified. In that case the newest saved version of that slug
 * is used, mirroring how the online path picks the newest version.
 */
export function useOfflineArticle(slug: string | undefined, pubkey: string | undefined): NostrEvent | undefined {
  const library = useOfflineLibraryStore();

  return useMemo(() => {
    if (!slug) return undefined;
    if (pubkey) return library[offlineKey(pubkey, slug)]?.event;

    return sortOfflineArticles(library)
      .map((entry) => entry.event)
      .filter((event) => getTagValue(event, 'd') === slug)
      .sort((a, b) => b.created_at - a.created_at)[0];
  }, [library, slug, pubkey]);
}

export interface UseOfflineLibraryResult {
  /** Saved articles, newest save first. */
  articles: OfflineArticle[];
  /** Total number of saved articles. */
  count: number;
  /** Whether this specific event's article (by `pubkey:d`) is saved. */
  isSaved: (event: NostrEvent) => boolean;
  /** Saves (or refreshes) an article for offline reading. Throws `OfflineQuotaError` if storage is full. */
  save: (event: NostrEvent, author?: OfflineAuthor) => void;
  /** Removes an article from the offline library. */
  remove: (event: NostrEvent) => void;
  /** Removes every saved article. */
  clear: () => void;
}

/**
 * Read/write access to the offline article library — wiki articles the
 * user has explicitly saved into `localStorage` so they remain readable
 * with no relay connection (on a plane, on an e-reader that's usually
 * offline, on a flaky connection).
 *
 * Each entry stores the complete signed event, so a saved article stays
 * independently verifiable and renders through exactly the same
 * pipeline as a freshly-fetched one — no separate "offline" rendering
 * path to drift out of sync.
 */
export function useOfflineLibrary(): UseOfflineLibraryResult {
  const library = useOfflineLibraryStore();

  const articles = useMemo(() => sortOfflineArticles(library), [library]);

  const isSaved = useCallback(
    (event: NostrEvent) => {
      const key = offlineKeyForEvent(event);
      return Boolean(key && library[key]);
    },
    [library],
  );

  const save = useCallback((event: NostrEvent, author?: OfflineAuthor) => {
    const key = offlineKeyForEvent(event);
    if (!key) return;
    mutate((current) => ({
      ...current,
      [key]: { event, savedAt: Date.now(), author },
    }));
  }, []);

  const remove = useCallback((event: NostrEvent) => {
    const key = offlineKeyForEvent(event);
    if (!key) return;
    mutate((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    mutate(() => ({}));
  }, []);

  return { articles, count: articles.length, isSaved, save, remove, clear };
}
